import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { BarChart2, Users, Store, FileText, AlertCircle } from '../../lib/lucide-proxy';
import { Link } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const PlatformDashboard = () => {
  const [stats, setStats] = useState({
    breweries: 0,
    users: 0,
    posts: 0,
    revenue: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // 1. Count Active Breweries
      const { count: breweryCount, error: breweryError } = await supabase
        .from('breweries')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      // 2. Count Users
      const { count: userCount, error: userError } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });

      // 3. Count Blog Posts
      const { count: postCount, error: postError } = await supabase
        .from('blog_posts')
        .select('*', { count: 'exact', head: true });

      // 4. Sum Total Revenue (from all orders)
      // Note: aggregating efficiently might require an RPC function for large datasets, 
      // but for now we fetch orders to sum on client or use a small query.
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select('total')
        .neq('status', 'cancelled');
      
      const totalRevenue = orders?.reduce((acc, order) => acc + Number(order.total), 0) || 0;

      if (breweryError || userError || postError || ordersError) {
        console.error('Error fetching platform stats');
      }

      setStats({
        breweries: breweryCount || 0,
        users: userCount || 0,
        posts: postCount || 0,
        revenue: totalRevenue
      });

    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center p-12"><LoadingSpinner /></div>;

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - Cerveja Delivery</title>
      </Helmet>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-dark-900">Painel Administrativo</h2>
            <p className="text-dark-600">Visão geral da plataforma</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-dark-500">Cervejarias Ativas</h3>
              <Store className="w-5 h-5 text-primary-500" />
            </div>
            <p className="text-3xl font-bold text-dark-900">{stats.breweries}</p>
            <Link to="/admin/accounts" className="text-xs text-primary-600 mt-2 block hover:underline">Gerenciar</Link>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-dark-500">Usuários</h3>
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-dark-900">{stats.users}</p>
            <Link to="/admin/accounts" className="text-xs text-blue-600 mt-2 block hover:underline">Ver todos</Link>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-dark-500">Posts no Blog</h3>
              <FileText className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-dark-900">{stats.posts}</p>
            <Link to="/admin/blog" className="text-xs text-green-600 mt-2 block hover:underline">Gerenciar conteúdo</Link>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-dark-500">Volume Total (GMV)</h3>
              <BarChart2 className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-3xl font-bold text-dark-900">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(stats.revenue)}
            </p>
            <span className="text-xs text-dark-400 mt-2 block">Acumulado histórico</span>
          </div>
        </div>

        {/* Actions Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-bold text-dark-900 mb-4">Ações Rápidas</h3>
            <div className="space-y-3">
              <Link to="/admin/blog" className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <FileText className="w-5 h-5 text-dark-500 mr-3" />
                <div>
                  <div className="font-medium text-dark-900">Gerenciar Blog</div>
                  <div className="text-xs text-dark-500">Criar, editar e aprovar posts</div>
                </div>
              </Link>
              <Link to="/admin/accounts" className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <Store className="w-5 h-5 text-dark-500 mr-3" />
                <div>
                  <div className="font-medium text-dark-900">Aprovar Cervejarias</div>
                  <div className="text-xs text-dark-500">Revisar novos cadastros</div>
                </div>
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-bold text-dark-900 mb-4">Alertas do Sistema</h3>
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-blue-800">Sistema Operacional</h4>
                <p className="text-xs text-blue-700 mt-1">Todos os serviços estão funcionando normalmente.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PlatformDashboard;